import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Brain, Shield, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useEnhancedContentAnalysis } from '@/hooks/useEnhancedContentAnalysis';

interface AnalysisVisualizationProps {
  result: any;
}

const AnalysisVisualization: React.FC<AnalysisVisualizationProps> = ({ result }) => {
  if (!result) return null;

  const getRiskColor = (score: number) => {
    if (score < 0.3) return 'text-green-600';
    if (score < 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRiskLevel = (score: number) => {
    if (score < 0.3) return 'Low';
    if (score < 0.7) return 'Medium';
    return 'High';
  };

  return (
    <div className="space-y-6">
      {/* Main Result */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            {result.shouldFilter ? (
              <XCircle className="h-8 w-8 text-red-500" />
            ) : (
              <CheckCircle className="h-8 w-8 text-green-500" />
            )}
            <div>
              <CardTitle className={result.shouldFilter ? 'text-red-600' : 'text-green-600'}>
                {result.shouldFilter ? 'Content Filtered' : 'Content Approved'}
              </CardTitle>
              <CardDescription>
                Confidence: {(result.confidence * 100).toFixed(1)}%
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        {result.reasons?.length > 0 && (
          <CardContent>
            <div className="space-y-2">
              <h4 className="font-medium">Reasons:</h4>
              {result.reasons.map((reason: string, index: number) => (
                <Badge key={index} variant="destructive" className="mr-2">
                  {reason}
                </Badge>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      <Tabs defaultValue="scores" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="scores">Risk Scores</TabsTrigger>
          <TabsTrigger value="emotions">Emotions</TabsTrigger>
          <TabsTrigger value="entities">Entities</TabsTrigger>
          <TabsTrigger value="providers">AI Models</TabsTrigger>
        </TabsList>

        <TabsContent value="scores" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Risk Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: 'Overall Risk', value: result.riskScore || 0 },
                { label: 'Toxicity', value: result.toxicityScore || 0 },
                { label: 'Spam', value: result.spamScore || 0 },
                { label: 'Manipulation', value: result.manipulationScore || 0 }
              ].map((score) => (
                <div key={score.label} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{score.label}</span>
                    <span className={`text-sm font-bold ${getRiskColor(score.value)}`}>
                      {getRiskLevel(score.value)} ({(score.value * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <Progress value={score.value * 100} className="h-2" />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emotions" className="space-y-4">
          {result.emotions && (
            <Card>
              <CardHeader>
                <CardTitle>Emotional Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(result.emotions).map(([emotion, value]: [string, any]) => (
                  <div key={emotion} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">{emotion}</span>
                      <span className="text-sm">{(value * 100).toFixed(1)}%</span>
                    </div>
                    <Progress value={value * 100} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="entities" className="space-y-4">
          {result.entities && (
            <div className="grid gap-4 md:grid-cols-2">
              {Object.entries(result.entities).map(([type, items]: [string, any]) => (
                <Card key={type}>
                  <CardHeader>
                    <CardTitle className="capitalize">{type}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {items?.length > 0 ? (
                        items.map((item: string, index: number) => (
                          <Badge key={index} variant="outline">
                            {item}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None detected</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          {result.providers && (
            <div className="grid gap-4">
              {Object.entries(result.providers).map(([provider, data]: [string, any]) => (
                <Card key={provider}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 capitalize">
                      <Brain className="h-5 w-5" />
                      {provider} Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Should Filter:</span>
                        <Badge variant={data.shouldFilter ? 'destructive' : 'secondary'}>
                          {data.shouldFilter ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Confidence:</span>
                        <span>{(data.confidence * 100).toFixed(1)}%</span>
                      </div>
                      {data.sentiment && (
                        <div className="flex justify-between">
                          <span>Sentiment:</span>
                          <Badge variant="outline" className="capitalize">
                            {data.sentiment}
                          </Badge>
                        </div>
                      )}
                      {data.topics?.length > 0 && (
                        <div className="space-y-2">
                          <span className="text-sm font-medium">Topics:</span>
                          <div className="flex flex-wrap gap-1">
                            {data.topics.map((topic: string, index: number) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const EnhancedContentAnalyzer: React.FC = () => {
  const [content, setContent] = useState('');
  const [result, setResult] = useState(null);
  const { analyzeContentEnhanced, loading, error } = useEnhancedContentAnalysis();

  const handleAnalyze = async () => {
    if (!content.trim()) return;

    const analysisResult = await analyzeContentEnhanced({
      content,
      platform: 'test',
      options: {
        providers: ['openai', 'anthropic', 'local'],
        includeEmotions: true,
        includeIntents: true,
        includeEntities: true,
        deepAnalysis: true
      }
    });

    if (analysisResult) {
      setResult(analysisResult);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Enhanced AI Content Analyzer</h1>
        <p className="text-muted-foreground">
          Analyze content with multiple AI models for comprehensive insights
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Content Input</CardTitle>
          <CardDescription>
            Enter the social media content you want to analyze
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Enter your content here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            className="resize-none"
          />
          <Button 
            onClick={handleAnalyze} 
            disabled={!content.trim() || loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing with AI Models...
              </>
            ) : (
              <>
                <Brain className="mr-2 h-4 w-4" />
                Analyze Content
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && <AnalysisVisualization result={result} />}
    </div>
  );
};